/* global Vue, BOOKING_APP_URI, CONTACT_URL, LOCALE, TRUST_YOU_API_KEY, AZURE_URI, PREFIX, DATA_LAYER, AlgoliaEvents */

import { changeTextCurrency, useCurrencyObserver } from '../../use-currency-observer'
import PropertyCardGallery from './PropertyCardGallery'
import { Price } from '../../classes/Price'
import CldImg from './CldImg'
import { BookingParams } from '../../classes/BookingParams'

export default {
  name: 'PropertyCard',
  components: { CldImg, PropertyCardGallery },
  template: `
<div class="property-card">
  <PropertyCardGallery v-if="showGallery && property.propertyListingGallery" :property="property" />
  <a v-else :href="property.link" :title="property.title" class="property-card__image">
    <CldImg :src="property.image ?? '/icons/hotel.svg'" :alt="property.title" set-height="true" />
    <img
        v-if="property.groupLogoCircle"
        :src="property.groupLogoCircle"
        :alt="property.groupTitle + ' logo'"
        class="property-card__group-logo"
        loading="lazy"
        width="44"
        height="40">
  </a>

  <div class="property-card__content">
    <span class="card__discount" v-show="discount">{{ discount }}</span>

    <div v-if="property.brandLogo" class="property-card__logos">
      <img
        :src="property.brandLogo"
        :alt="'Maistra '+ property.brand"
        class="property-card__brand-logo"
        loading="lazy"
        width="168"
        height="20">
    </div>
    <a class="h3" :href="property.link">{{ property.title }}</a>

    <div class="card__stars">
      <span v-if="property.stars" class="stars" :class="'theme--' + property.brand">
        <i v-for="n in property.stars" class="icon-star"></i>
      </span>
      <span>{{ property.destination }}</span>
    </div>

    <div v-show="!customDescription">
      <p class="no-margin-top">{{ property.description }}</p>
      <a class="arrow-button" :href="property.link + '?' + bookingParams.urlString">
        {{ $t('discoverMore') }}
        <i class="icon-arrow-right"></i>
      </a>
    </div>
    <div v-show="customDescription">
      <p class="no-margin-top" v-html="customDescription"></p>
    </div>
  </div>

  <div class="property-card__price">
    <a
      v-if="property.trustYouId"
      :href="'https://www.trustyou.com/about/meta-review?ty_id=' + property.trustYouId + '&lang=' + trustYouLocale"
      target="_blank" title="TrustYou Score">
      <iframe
        :src="'https://api.trustyou.com/hotels/' + property.trustYouId + '/seal.html?size=m&key=' + TRUST_YOU_API_KEY + '&lang=' + trustYouLocale"
        width="100%"
        title="TrustYou Score"
        height="60"
        frameborder="0"
        id="iFrameResizer2"
        scrolling="no"
        loading="lazy"
        style="overflow: hidden; pointer-events: none;">
      </iframe>
    </a>

    <template v-if="!resolvingAvailability">
      <h3 v-if="property.availablePriceFrom && hasPrice" class="unit-card__price">
        <small>{{ $t('perUnit') }}</small>
        <span>{{ $t('from') }}</span>
        {{ property.availablePriceFrom.display }}
      </h3>

      <button
        v-if="property.availablePriceFrom"
        class="button button--primary"
        @click="openBooking">
        {{ $t('bookNow') }}
      </button>

      <template v-else-if="isAvailabilityError">
        <i>{{ $t('unitDemand.availabilityError') }}</i>
        <a :href="CONTACT_URL + '?property=' + property.phobsId" class="button button--primary">{{ $t('unitDemand.contactButton') }}</a>
      </template>

      <template v-else>
        <i>{{ $t('unitDemand.contactText') }}</i>
        <button class="button button--outline" @click="openCheckRates">{{ $t('unitDemand.modifySearch') }}</button>
        <a :href="CONTACT_URL + '?property=' + property.phobsId" class="button button--primary">{{ $t('unitDemand.contactButton') }}</a>
      </template>
    </template>

    <img v-else src="https://res.cloudinary.com/maistra/image/upload/v1658495232/Assets/auth-in-progress-dark.svg" width="80" loading="lazy" />
  </div>
</div>
  `,
  props: {
    property: {
      type: Object,
      required: true,
    },
    bookingParams: {
      type: Object,
      default: {},
    },
    customDescription: {
      type: String,
      default: '',
    },
    discount: {
      type: String,
      default: '',
    },
    hasPrice: {
      type: Boolean,
      default: true,
    },
    showGallery: {
      type: Boolean,
      default: false,
    },
    fallbackAvailability: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const resolvingAvailability = Vue.ref(true)
    const isAvailabilityError = Vue.ref(false)
    const firstAvailableDate = Vue.ref(false)
    const discount = Vue.ref('')
    const currency = useCurrencyObserver()
    const trustYouLocale = LOCALE === 'si' ? 'sl' : LOCALE

    const openCheckRates = () => {
      window.location.href = `#/check-rates?step=3&propertyId=${props.property.phobsId}&${props.bookingParams.urlString}`
    }

    const openBooking = () => {
      if (AlgoliaEvents) {
        AlgoliaEvents.convertedFilters({
          eventName: 'Booking Intent',
          property: [props.property.groupTitle],
          brand: [props.property.brand],
          destination: [props.property.destination],
          propertyType: [props.property.menuGroup],
          campingType: props.property.campingType ? [props.property.campingType] : [],
          holidayType: props.property.holidayType ? [props.property.holidayType] : [],
        })
      }

      const { phobsId, title, brand, menuGroup, destination } = props.property
      DATA_LAYER.wwwBookNow({
        id: phobsId,
        name: title,
        brand,
        category: `${menuGroup}/${destination}`,
      })
      const bookingParams = new BookingParams({ ...props.bookingParams })
      if (firstAvailableDate.value && props.fallbackAvailability) {
        bookingParams.startDate = new Date(firstAvailableDate.value)
      }
      window.location.href = `${BOOKING_APP_URI}${PREFIX}/step1?propertyId=${props.property.phobsId}&${bookingParams.urlString}&currency=${currency.value}`
    }

    async function loadAvailibilityAsync () {
      if (!props.property.phobsId) {
        resolvingAvailability.value = false
        isAvailabilityError.value = true
        return
      }
      resolvingAvailability.value = true
      props.property.availablePriceFrom = null

      const { accessCode, startDate, endDate } = props.bookingParams

      // endDate.setDate(endDate.getDate() - 1)

      const queryParams = {
        id: props.property.phobsId,
        accessCode: encodeURIComponent(accessCode),
        start: startDate.toISOString().substring(0, 10),
        end: endDate.toISOString().substring(0, 10),
        locale: LOCALE,
      }

      if (queryParams.start < new Date().toISOString().substring(0, 10)) {
        resolvingAvailability.value = false
        return
      }

      const availability = await requestAvailability(queryParams)
      resolvingAvailability.value = false

      if (availability != null && availability.rateFrom !== null) {
        props.property.availablePriceFrom = new Price(availability.rateFrom, availability.currency)
      }

      if (props.fallbackAvailability && availability.firstAvailableDate) {
        firstAvailableDate.value = availability.firstAvailableDate.date
        props.property.availablePriceFrom = new Price(availability.firstAvailableDate.rateFrom, availability.firstAvailableDate.currency)
      }
    }

    async function requestAvailability (queryParams) {
      try {
        const response = await fetch(`${AZURE_URI}GetPropertyAvailability?` + Object.keys(queryParams).map(k => `${k}=${queryParams[k]}`).join('&'))
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        return await response.json()
      } catch (e) {
        isAvailabilityError.value = true
      }
    }

    Vue.watch(props.bookingParams, () => loadAvailibilityAsync(), { immediate: true })

    Vue.watch(currency, () => {
      const sourceDiscount = props.discount
      discount.value = changeTextCurrency(sourceDiscount)
    }, { immediate: true })

    return {
      LOCALE,
      trustYouLocale,
      CONTACT_URL,
      TRUST_YOU_API_KEY,
      resolvingAvailability,
      isAvailabilityError,
      openCheckRates,
      openBooking,
      discount,
    }
  },
}
